import React, { useContext, useState } from 'react'
import { ListMenu, ListMenuButton, Modal, Popover, Spinner, Text, View } from 'oio-react'
import PropTypes from 'prop-types'
import { useMessageList, useRemoveMessage, useRemoveMessageReply } from 'src/graphql/hooks'
import { InitiativeHierarchyContext } from 'src/libraries/contexts/InitiativeHierarchy'
import MessageInput from '~/Messages/components/Input'
import MessageListItem from '~/Messages/components/ListItem'
import MessageEdit from '~/Messages/components/MessageEdit'
import MessageReplyEdit from '~/Messages/components/MessageReplyEdit'

const MessagesFeed = ({
   allowFileAttachments,
   allowMarkClosingMessage,
   allowMessageCreate,
   closingMessageId,
   currentUserCanModerate,
   displayMode,
   hasUnreadEntries,
   messageLastViewed,
   messageInputToolbarConfig,
   onCloseMessageButtonClick
}) => {
   const [modalIsOpen, setModalIsOpen] = useState(false)
   const [popoverConfig, setPopoverConfig] = useState({
      anchor: null,
      focusedValue: null,
      isOpen: false
   })

   const { initiative } = useContext(InitiativeHierarchyContext)
   const { messageList, fetchMore, loading, networkStatus } = useMessageList({
      limit: 30,
      sortBy: 'dateDesc',
      threadId: initiative.thread.id
   })

   const { removeMessage } = useRemoveMessage()
   const { removeMessageReply } = useRemoveMessageReply()

   const handleRemove = async () => {
      const { messageId, replyId, targetType } = popoverConfig.focusedValue

      if (targetType === 'reply') {
         await removeMessageReply({ messageId, replyId })
      } else if (targetType === 'message') {
         await removeMessage({ id: messageId })
      }

      setPopoverConfig({
         ...popoverConfig,
         focusedValue: null
      })
   }

   const handleLoadMore = () => {
      fetchMore({
         variables: {
            start: messageList.listInfo.nextCursor
         },
         updateQuery: (prevResult, { fetchMoreResult }) => {
            if (!fetchMoreResult) {
               return prevResult
            }

            return {
               ...prevResult,
               messageList: {
                  ...prevResult.messageList,
                  items: [
                     ...prevResult.messageList.items,
                     ...fetchMoreResult.messageList.items
                  ],
                  listInfo: fetchMoreResult.messageList.listInfo
               }
            }
         }
      })
   }

   const initialLoading = networkStatus <= 2
   if (initialLoading) {
      return (
         <View
            width="100%"
            textAlign="center"
            padding="30px"
            backgroundColor="#eee"
            borderRadius="6px">
            <Spinner color="#ccc" width="24px" height="24px" />
         </View>
      )
   }

   // Message List Items - Reverse order if sorted by desc or asc order
   const messageListItems = displayMode === 'feed'
      ? messageList.items
      : [...messageList.items].reverse()

   return (
      <View>
         {allowMessageCreate && displayMode === 'feed' && (
            <MessageInput
               allowFileAttachments={allowFileAttachments}
               initiativeId={initiative.id}
               inputToolbarConfig={messageInputToolbarConfig}
            />
         )}
         {messageList.items.length === 0 && (
            <View float="left" width="100%" padding="30px 0px" textAlign="center">
               <Text size="2" color="#777">
                  No comments have been posted yet
               </Text>
            </View>
         )}
         {displayMode === 'conversation' && messageList.listInfo.hasNext && (
            <View
               onClick={handleLoadMore}
               float="left"
               width="100%"
               borderBottom="1px solid #eee"
               padding="20px 0px"
               style={{ cursor: 'pointer' }}>
               <Text size="1.5" weight="medium" color="#888">
                  {loading ? 'Loading...' : 'View earlier comments...'}
               </Text>
            </View>
         )}
         {messageListItems.map((message, index) => (
            <MessageListItem
               key={message.id}
               id={message.id}
               allowMarkClosingMessage={allowMarkClosingMessage}
               allowMessageCreate={allowMessageCreate}
               anon={message.anon}
               authorAvatarUrl={message?.author?.avatarMedia?.file.thumbnailUrlW48}
               authorId={message?.author?.id}
               authorName={message?.author?.fullName}
               body={message.body}
               borderStyle={index === 0 ? 'none' : undefined}
               currentUserCanModerate={currentUserCanModerate}
               edited={message.edited}
               dateAdded={message.dateAdded}
               isClosingMessage={closingMessageId === message.id}
               isLikedByMe={message.isLikedByMe}
               isMine={message.isMine}
               numLikes={message.numLikes}
               onCloseMessageButtonClick={() => onCloseMessageButtonClick(message.id)}
               onOptionsButtonClick={(e) => {
                  setPopoverConfig({
                     anchor: e.currentTarget,
                     focusedValue: {
                        targetType: 'message',
                        isMine: message.isMine,
                        messageId: message.id,
                        messageBody: message.body
                     },
                     isOpen: true
                  })
               }}
               onReplyOptionsButtonClick={(e, replyId) => {
                  const reply = message.replies.find(r => r.id === replyId)

                  setPopoverConfig({
                     anchor: e.currentTarget,
                     focusedValue: {
                        targetType: 'reply',
                        isMine: reply.isMine,
                        messageId: message.id,
                        replyBody: reply.body?.elements?.[0]?.body,
                        replyId
                     },
                     isOpen: true
                  })
               }}
               replies={message.replies}
               showNewMessageAnchor={hasUnreadEntries && messageLastViewed === message.id}
            />
         ))}
         {displayMode === 'feed' && messageList.listInfo.hasNext && (
            <View
               onClick={handleLoadMore}
               float="left"
               width="100%"
               borderTop="1px solid #eee"
               padding="20px 0px"
               style={{ cursor: 'pointer' }}>
               <Text size="1.5" weight="medium" color="#888">
                  {loading ? 'Loading...' : 'View earlier comments...'}
               </Text>
            </View>
         )}
         {allowMessageCreate && displayMode === 'conversation' && (
            <View float="left" width="100%">
               <MessageInput
                  allowFileAttachments={allowFileAttachments}
                  initiativeId={initiative.id}
                  inputToolbarConfig={messageInputToolbarConfig}
               />
            </View>
         )}
         {/* Message Popover */}
         <Popover
            anchorElement={popoverConfig.anchor}
            anchorOriginHorizontal="right"
            anchorOriginVertical="top"
            backgroundColor="#fff"
            borderRadius="6px"
            width="120px"
            onBodyClick={() => setPopoverConfig({
               ...popoverConfig,
               isOpen: false
            })}
            open={popoverConfig.isOpen}>
            <View
               float="left"
               width="100%"
               backgroundColor="#fff"
               borderRadius="6px"
               boxShadow="3px 3px 12px rgba(0,0,0,0.2)">
               <ListMenu buttonSize="sm">
                  {popoverConfig.focusedValue?.isMine && (
                     <ListMenuButton
                        name="Edit"
                        onClick={() => setModalIsOpen(true)}
                     />
                  )}
                  {(popoverConfig.focusedValue?.isMine || currentUserCanModerate) && (
                     <ListMenuButton
                        name="Delete"
                        onClick={handleRemove}
                     />
                  )}
               </ListMenu>
            </View>
         </Popover>
         <Modal
            borderRadius="6px"
            overlayBackgroundColor="rgba(0,0,0,0.85)"
            width="100%[a-b] 600px[c-f]"
            height="100%[a-b] 85%[c-f]"
            onCloseTrigger={() => setModalIsOpen(false)}
            open={modalIsOpen}>
            {popoverConfig.focusedValue && (
               <>
                  {popoverConfig.focusedValue.targetType === 'message' && (
                     <MessageEdit
                        allowFileAttachments={allowFileAttachments}
                        initiativeId={initiative.id}
                        inputToolbarConfig={messageInputToolbarConfig}
                        messageBody={popoverConfig.focusedValue.messageBody}
                        messageId={popoverConfig.focusedValue.messageId}
                        onCloseButtonClick={() => setModalIsOpen(false)}
                        onUpdate={() => setModalIsOpen(false)}
                     />
                  )}
                  {popoverConfig.focusedValue.targetType === 'reply' && (
                     <MessageReplyEdit
                        messageId={popoverConfig.focusedValue.messageId}
                        replyBody={popoverConfig.focusedValue.replyBody}
                        replyId={popoverConfig.focusedValue.replyId}
                        onCloseButtonClick={() => setModalIsOpen(false)}
                        onUpdate={() => setModalIsOpen(false)}
                     />
                  )}
               </>
            )}
         </Modal>
      </View>
   )
}

MessagesFeed.propTypes = {
   allowFileAttachments: PropTypes.bool,
   allowMarkClosingMessage: PropTypes.bool,
   allowMessageCreate: PropTypes.bool,
   closingMessageId: PropTypes.string,
   currentUserCanModerate: PropTypes.bool.isRequired,
   // Coversation display mode shows messages with the newest at the bottom
   // Feed display mode shows newest messages at the top
   displayMode: PropTypes.oneOf(['conversation', 'feed']).isRequired,
   hasUnreadEntries: PropTypes.bool,
   messageLastViewed: PropTypes.string,
   messageInputToolbarConfig: PropTypes.oneOf(['advanced', 'textBasic']),
   onCloseMessageButtonClick: PropTypes.func
}

MessagesFeed.defaultProps = {
   allowFileAttachments: false,
   allowMarkClosingMessage: false,
   allowMessageCreate: true,
   closingMessageId: undefined,
   hasUnreadEntries: false,
   messageLastViewed: undefined,
   messageInputToolbarConfig: 'textBasic',
   onCloseMessageButtonClick: undefined
}

export default MessagesFeed
